import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

// --- ANIMATIONS & SPINNER ---
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #0e141b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
`;

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 20px;
  box-sizing: border-box;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const BackBtn = styled.button`
  background: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #0e141b;
  transition: all 0.2s ease;
  &:hover {
    background: #cbd5e1;
  }
`;

const Title = styled.h2`
  color: #0d141c;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.875rem;
  outline: none;
  width: 100%;
  max-width: 300px;
  background: white;

  &:focus {
    border-color: #0e141b;
  }
`;

const TargetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const TargetHeader = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0e141b;
  text-transform: capitalize;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 8px;
`;

const ModelRowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const ModelLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${(props) => props.$color || '#0f172a'};
`;

const ModelDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.$color || '#0e141b'};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px;
  width: 100%;
`;

const FeatureBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
`;

const FeatureLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  word-break: break-word;
`;

const FeatureValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  margin-top: 2px;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  padding: 8px 0;
`;

// --- UTILS ---
function shortenValue(val) {
  if (typeof val === 'string' && isNaN(val)) return val;
  return Number(val).toFixed(2);
}

const GEMS = ["sapphire", "emerald", "opal", "amethyst"];
const VIEWS = ['points', 'assists', 'rebounds'];

const GEM_COLORS = {
  rock: '#4f46e5',
  ruby: '#e11d48',
  diamond: '#38bdf8',
  quartz: '#ec4899',
  beryl: '#10b981',
  mica: '#f59e0b',
  onyx: '#334155',
  pearl: '#64748b',
  garnet: '#9f1239',
  zircon: '#06b6d4',
  zinc: '#94a3b8',
};

const IGNORED_METRICS = new Set(['y_train_range', 'y_train_std', 'y_test_range', 'y_true']);

// --- COMPONENT ---
export const Features = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [allFeatureStore, setAllFeatureStore] = useState({});
  const [pendingCount, setPendingCount] = useState(0);

  const fetchSingleCombo = async (targetView, gem) => {
    try {
      const res = await axios({
        method: 'post',
        url: 'https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull',
        withCredentials: false,
        data: {
          option: 'features',
          target: targetView,
          gem,
        },
      });
      return res?.data?.data || res?.data || [];
    } catch (err) {
      console.error(`Error fetching features for ${targetView}_${gem}`, err);
      return [];
    }
  };

  useEffect(() => {
    let isSubscribed = true;
    const totalRequests = GEMS.length * VIEWS.length;
    setPendingCount(totalRequests);

    GEMS.forEach((g) => {
      VIEWS.forEach((v) => {
        fetchSingleCombo(v, g).then((rawData) => {
          if (!isSubscribed) return;

          const formatted = Array.isArray(rawData) ? rawData : Object.entries(rawData || {});

          setAllFeatureStore((prev) => ({
            ...prev,
            [`${v}_${g}`]: formatted,
          }));

          setPendingCount((cnt) => Math.max(0, cnt - 1));
        });
      });
    });

    return () => {
      isSubscribed = false;
    };
  }, []);

  return (
    <PageContainer>
      <HeaderSection>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {onBack && <BackBtn onClick={onBack}>← Back</BackBtn>}
          <Title>Model Features Overview</Title>
          {pendingCount > 0 && <Spinner title="Loading datasets..." />}
        </div>

        <SearchInput
          type="text"
          placeholder="Filter metric name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </HeaderSection>

      {/* RENDER TARGET SECTIONS (Points, Assists, Rebounds) */}
      {VIEWS.map((targetView) => (
        <TargetSection key={targetView}>
          <TargetHeader>{targetView}</TargetHeader>

          {/* RENDER EACH GEM MODEL ROW */}
          {GEMS.map((gem) => {
            const activeKey = `${targetView}_${gem}`;
            const isLoaded = activeKey in allFeatureStore;
            const features = allFeatureStore[activeKey] || [];

            // Filter out ignored keys & filter search query
            const filtered = features.filter(([key]) => {
              const metricKey = String(key);
              if (IGNORED_METRICS.has(metricKey)) return false;
              if (searchQuery.trim()) {
                return metricKey.toLowerCase().includes(searchQuery.toLowerCase());
              }
              return true;
            });

            return (
              <ModelRowContainer key={gem}>
                <ModelLabel $color={GEM_COLORS[gem]}>
                  <ModelDot $color={GEM_COLORS[gem]} />
                  {gem}
                </ModelLabel>

                {!isLoaded ? (
                  <LoadingRow>
                    <Spinner /> Loading {gem} metrics...
                  </LoadingRow>
                ) : filtered.length === 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    {searchQuery ? 'No matching metrics' : 'No feature metrics available'}
                  </div>
                ) : (
                  <FeatureGrid>
                    {filtered.map(([metricKey, val]) => (
                      <FeatureBox key={metricKey}>
                        <FeatureLabel>{metricKey}</FeatureLabel>
                        <FeatureValue>{shortenValue(val)}</FeatureValue>
                      </FeatureBox>
                    ))}
                  </FeatureGrid>
                )}
              </ModelRowContainer>
            );
          })}
        </TargetSection>
      ))}
    </PageContainer>
  );
};

export default Features;